import { workspaces as workspacesApi } from "apis/callables";
import { SubscriptionStatus } from "common/constants";
import validateEduDomains from "common/utils/validateEduDomains";
import Cookies from "js-cookie";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import StorageModel from "js/core/storage/storageModel";
import isConnected from "js/core/utilities/isConnected";
import { auth } from "js/firebase/auth";
import { ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import { getWorkspacesMetadata } from "js/controllers/WorkspaceController";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.USER);

const User = StorageModel.extend({
    root: "users",

    initialize() {
        if (this.id) {
            // cookie for analytics
            Cookies.set("userId", this.id);
        }
    },

    async generateLoadPromise(...args) {
        // Awaiting the base generate promise first
        await StorageModel.prototype.generateLoadPromise.apply(this, ...args);

        const user = auth().currentUser;
        if (this.get("email") !== user.email) {
            // we found an issue where there are some users (who signed up
            // through google sign in) in our user table that do not have an
            // email even though the auth table does have an email, so this
            // is to backfill the user table with emails from the auth table.
            this.update({ email: user.email });
        }

        // fetch trial detail from API
        if (isConnected.connected) {
            try {
                // note: these details apply to the personal workspace only
                const detail = await Api.trialDetail.get({ t: new Date().getTime() });
                this.trialStatus = detail.status;
                this.hasTakenTrial = detail.hasTakenTrial;
                this.analyticsPersonalPlan = detail.analyticsCurrentPlan;
                this.teamPlans = detail.teamPlans;
                this.personalPlan = detail.personalPlan;
            } catch (err) {
                logger.error(err, "[user] load() Api.trialDetail.get() failed", { id: this.id });
            }

            try {
                await Promise.all([
                    this.loadWorkspacesMetadata(),
                    this.loadAvailableTrialDays()
                ]);
            } catch (err) {
                logger.error(err, "[user] load() workspacesApi.getWorkspacesMetadata() failed", { id: this.id });
            }
        }
    },

    async loadWorkspacesMetadata() {
        this.workspacesMetadata = await getWorkspacesMetadata();
    },

    async loadAvailableTrialDays() {
        const availableTrialDays = await workspacesApi.getAvailableTrialDays({});
        this.availableTrialDays = availableTrialDays;
    },

    loadMinimum() {
        return StorageModel.prototype.load.apply(this, arguments);
    },

    getEmail() {
        return this.getAuthUser().email;
    },

    getAuthUser() {
        return auth().currentUser;
    },

    getAvatarUrl() {
        let { photoURL } = this.getAuthUser() || {};
        return this.get("avatarUrl") || photoURL;
    },

    getDisplayName() {
        return this.getAuthUser().displayName || this.getAuthUser().email;
    },

    checkIfUserAndPresentationBelongToSameOrg(presentationOrgId) {
        return this.has("orgs") && this.get("orgs")[presentationOrgId];
    },

    isEligibleForEduPlan(email) {
        return !validateEduDomains(email || this.get("email"));
    },

    async updateEmailAddress(email) {
        let user = this.getAuthUser();
        await auth().updateEmail(user, email);
        this.update({ email });
        if (this.analyticsPersonalPlan === "edu" && !this.isEligibleForEduPlan(email)) {
            ShowMessageDialog({
                title: "You are currently on the Edu plan. Changing your email will downgrade you to the Basic plan.",
                message: "If you are still a student, you can reapply for the education plan after validating your new email address."
            });
            await Api.eduPlan.delete();
        }
        return auth().sendEmailVerification(user);
    },

    waitForVerifiedEmail() {
        let user = this.getAuthUser();
        let checkVerifiedInterval;
        return new Promise(resolve => {
            clearInterval(checkVerifiedInterval);
            checkVerifiedInterval = setInterval(async () => {
                if (user) {
                    await user.reload();
                    if (user.emailVerified) {
                        await auth().currentUser.getIdToken(true);
                        clearInterval(checkVerifiedInterval);
                        resolve();
                    }
                } else {
                    clearInterval(checkVerifiedInterval);
                }
            }, 5000);
        });
    },

    getLibrarySettings() {
        return _.defaults(_.cloneDeep(this.get("librarySettings")), {
            viewType: "grid",
            sortField: "modifiedAt",
            sortReverse: true,
            thumbnailSize: 300,
            recentPresentations: [],
            scaleInterp: 0.5,
        });
    },

    hasSubscription() {
        const subscriptionStatus = this.get("subscriptionStatus");
        return [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING, SubscriptionStatus.PAST_DUE].includes(subscriptionStatus);
    },

    async isEduUser() {
        return await Api.eduPlan.get({
            action: "isEduUser"
        });
    },

    async toObject() {
        const attrs = await StorageModel.prototype.toObject.call(this);
        //base64 encode images
        const photoURL = this.getAuthUser().photoURL;
        if (photoURL) {
            try {
                await fetch(photoURL);
            } catch {
                // noop
            }
        }
        return attrs;
    },

    async addPresentation(presentationId) {
        this.update({ presentations: { [presentationId]: true } });
        await this.updatePromise;
    },

    getPresentationIds() {
        return Object.keys(this.get("presentations") ?? {}).filter(key => !["id", "_changeId", "modifiedAt", "createdAt"].includes(key));
    }
});

export {
    User
};

